// PHASE_1 — Self-contained section with two toggles (push + email) for
// marketing notifications. Loads via GET /auth/me/preferences and writes
// via PATCH. Errors are surfaced through sonner toasts; mid-flight calls
// disable the toggles.
//
// Web counterpart of mobile/src/app/components/settings/marketing-preferences-section.tsx
// — keep them in sync if you add channels.

import { Mail, Megaphone } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  DEFAULT_MARKETING_PREFERENCES,
  getMarketingPreferences,
  type MarketingPreferences,
  updateMarketingPreferences,
} from "@/app/api/auth-client";
import { SettingsSection, type SettingItem } from "./settings-section";

export function MarketingPreferencesSection() {
  const [prefs, setPrefs] = useState<MarketingPreferences>(() => ({
    ...DEFAULT_MARKETING_PREFERENCES,
    user_id: "",
    status: "active",
  }));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<"push" | "email" | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getMarketingPreferences();
        if (!cancelled) setPrefs(p);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = useCallback(
    async (channel: "push" | "email", next: boolean) => {
      setPending(channel);
      const body =
        channel === "push"
          ? { marketing_push_opt_in: next }
          : { marketing_email_opt_in: next };
      const previous = prefs;
      setPrefs({
        ...prefs,
        marketing_push_opt_in:
          channel === "push" ? next : prefs.marketing_push_opt_in,
        marketing_email_opt_in:
          channel === "email" ? next : prefs.marketing_email_opt_in,
      });
      try {
        const updated = await updateMarketingPreferences(body);
        setPrefs(updated);
      } catch (e) {
        setPrefs(previous);
        const msg = e instanceof Error ? e.message : String(e);
        toast.error("Não foi possível guardar a preferência.", { description: msg });
      } finally {
        setPending(null);
      }
    },
    [prefs],
  );

  if (error) {
    return (
      <div>
        <h2 className="text-sm font-medium text-muted-foreground mb-3 px-2">
          Marketing
        </h2>
        <div className="bg-card rounded-xl shadow-sm border border-destructive/40 px-4 py-4 text-sm text-destructive">
          {error}
        </div>
      </div>
    );
  }

  const items: SettingItem[] = [
    {
      icon: Megaphone,
      label: "Notificações de promoções (push)",
      subtitle:
        "Receber pushes sobre ofertas e novidades. Pode mudar a qualquer momento.",
      action: () => toggle("push", !prefs.marketing_push_opt_in),
      toggle: true,
      value: prefs.marketing_push_opt_in,
      disabled: loading || pending === "push",
    },
    {
      icon: Mail,
      label: "Resumo semanal por email",
      subtitle: "Email com novidades curadas para você.",
      action: () => toggle("email", !prefs.marketing_email_opt_in),
      toggle: true,
      value: prefs.marketing_email_opt_in,
      disabled: loading || pending === "email",
    },
  ];

  return <SettingsSection title="Marketing" items={items} />;
}
